import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import { useBranding } from '../../context/BrandingContext'
import { NSField, NSSelect, NSPrefix, NSDrop } from '../shared/ns-kit'
import { I } from '../shared/ns-icons'

const STEPS = 4
const INDUSTRIES = ['Food & Beverage', 'Retail & E-commerce', 'Hospitality', 'Health & Wellness', 'Professional Services', 'Other']
const DEFAULT_COLORS = ['#6C5CE7', '#2BD9A6']

// Normalize whatever the profile returns into a 2-slot hex array.
const toColorPair = (val) => {
  const arr = Array.isArray(val) ? val : (typeof val === 'string' && val.trim() ? (() => { try { return JSON.parse(val) } catch { return [] } })() : [])
  return [arr[0] || DEFAULT_COLORS[0], arr[1] || DEFAULT_COLORS[1]]
}

const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const branding = useBranding()
  const { cmsName } = branding
  const logoSrc = branding.logoUrl || branding.logoUrlLight
  const [step, setStep] = useState(0)
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    company_name: '', email: '', phone: '', address: '', description: '',
    domain: '', industry: INDUSTRIES[0], logo: null, brand_colors: DEFAULT_COLORS
  })
  const [logoUploaded, setLogoUploaded] = useState(false)
  const [errors, setErrors] = useState({})

  const next = () => setStep(s => Math.min(s + 1, STEPS - 1))
  const back = () => setStep(s => Math.max(s - 1, 0))

  const { data: checklist, isLoading } = useQuery(
    'onboarding-checklist',
    () => apiService.get('/clients/onboarding/checklist').then(res => res.data),
    { refetchOnWindowFocus: false, enabled: isOpen }
  )

  useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data),
    {
      enabled: isOpen,
      onSuccess: (data) => {
        setFormData(prev => ({
          ...prev,
          company_name: data.company_name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          description: data.description || '',
          domain: data.domain || '',
          industry: data.industry && INDUSTRIES.includes(data.industry) ? data.industry : prev.industry,
          brand_colors: toColorPair(data.brand_colors)
        }))
      }
    }
  )

  // ── Mutations (wiring preserved) ──────────────────────────────────────────
  const updateProfileMutation = useMutation(
    async (profileData) => {
      const response = await apiService.put('/clients/profile', profileData)
      await apiService.post('/clients/onboarding/update', { itemId: 'about', completed: true })
      if (profileData.domain && profileData.domain.trim()) {
        await apiService.post('/clients/onboarding/update', { itemId: 'domain', completed: true })
      }
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        next()
      },
      onError: (error) => setErrors(prev => ({ ...prev, profile: error.response?.data?.error || 'Failed to save your details' }))
    }
  )

  const uploadLogoMutation = useMutation(
    async (logoFile) => {
      const fd = new FormData()
      fd.append('logo', logoFile)
      const response = await apiService.post('/clients/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      await apiService.post('/clients/onboarding/update', { itemId: 'logo', completed: true })
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboarding-checklist')
        setLogoUploaded(true)
        setErrors(prev => ({ ...prev, logo: null }))
      },
      onError: (error) => setErrors(prev => ({ ...prev, logo: error.response?.data?.error || 'Failed to upload logo' }))
    }
  )

  const updateBrandColorsMutation = useMutation(
    async (colors) => {
      const response = await apiService.put('/clients/profile', { brand_colors: colors })
      await apiService.post('/clients/onboarding/update', { itemId: 'brand_color', completed: true })
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        next()
      },
      onError: (error) => setErrors(prev => ({ ...prev, brand_color: error.response?.data?.error || 'Failed to save brand colours' }))
    }
  )

  const completeOnboardingMutation = useMutation(
    () => apiService.post('/clients/onboarding/complete'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboarding-checklist')
        queryClient.invalidateQueries('client-profile')
        if (onComplete) onComplete()
        if (onClose) onClose()
      }
    }
  )

  // ── Step handlers ─────────────────────────────────────────────────────────
  const setColor = (i, hex) => setFormData(prev => {
    const c = [...prev.brand_colors]; c[i] = hex; return { ...prev, brand_colors: c }
  })

  const handleLogoFiles = (files) => {
    const file = files?.[0]
    if (!file) return
    setFormData(prev => ({ ...prev, logo: file }))
    setLogoUploaded(false)
    uploadLogoMutation.mutate(file)
  }

  const handleCompanyNext = () => {
    if (!formData.company_name.trim()) {
      setErrors(prev => ({ ...prev, profile: 'Company name is required' }))
      return
    }
    setErrors(prev => ({ ...prev, profile: null }))
    updateProfileMutation.mutate({
      company_name: formData.company_name,
      industry: formData.industry,
      domain: formData.domain,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      description: formData.description
    })
  }

  const handleBrandNext = () => updateBrandColorsMutation.mutate(formData.brand_colors)

  if (!isOpen || isLoading || !checklist) return null
  if (checklist.onboardingCompleted) return null

  const savingCompany = updateProfileMutation.isLoading
  const savingBrand = updateBrandColorsMutation.isLoading

  return (
    <div className="drawer-backdrop" style={{ justifyContent: 'center', alignItems: 'center', animation: 'none' }}>
      <div className="card" style={{ width: 540, maxWidth: '94vw', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
        {/* progress bar */}
        <div style={{ height: 3, background: 'var(--surface-2)' }}>
          <div style={{ height: '100%', width: `${((step + 1) / STEPS) * 100}%`, background: 'var(--accent)', transition: 'width 0.3s ease' }} />
        </div>

        <div style={{ padding: 32, minHeight: 380, display: 'flex', flexDirection: 'column' }}>
          {step === 0 && (
            <div style={{ textAlign: 'center', margin: 'auto 0' }}>
              <div style={{ width: 72, height: 72, borderRadius: 20, margin: '0 auto 22px', background: 'linear-gradient(135deg, #A78BFA, #5B7CFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px -10px var(--accent-glow)' }}>
                {logoSrc
                  ? <img src={logoSrc} alt={cmsName} style={{ width: 46, height: 46, objectFit: 'contain' }} />
                  : <svg viewBox="0 0 24 24" width="38" height="38" fill="none"><path d="M8 8.5c1.6-2 6.4-2 8 0M8 12c1.6-2 6.4-2 8 0M8 15.5c1.6-2 6.4-2 8 0" stroke="white" strokeWidth="1.6" strokeLinecap="round" /></svg>}
              </div>
              <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' }}>Welcome to {cmsName}</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, marginTop: 12, maxWidth: 380, marginInline: 'auto' }}>
                Your workspace is ready. Let's get a few details so your site and brand are ready to go — it takes about a minute.
              </p>
              <button className="btn btn-primary" style={{ marginTop: 26, padding: '11px 22px' }} onClick={next}>Let's get started <I.arrowRight /></button>
            </div>
          )}

          {step === 1 && (
            <div style={{ margin: 'auto 0', width: '100%' }}>
              <div className="eyebrow" style={{ textAlign: 'center' }}>Step 2 of 4</div>
              <h2 style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-0.02em', textAlign: 'center', marginTop: 6 }}>Tell us about your company</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 24 }}>
                <NSField label="Company name">
                  <input className="input" value={formData.company_name} onChange={e => setFormData(prev => ({ ...prev, company_name: e.target.value }))} placeholder="Acme Inc." />
                </NSField>
                <NSField label="Industry">
                  <NSSelect value={formData.industry} options={INDUSTRIES} onChange={e => setFormData(prev => ({ ...prev, industry: e.target.value }))} />
                </NSField>
                <NSField label="Website" hint="if you have one">
                  <NSPrefix prefix="https://" value={formData.domain} placeholder="company.com" onChange={e => setFormData(prev => ({ ...prev, domain: e.target.value }))} />
                </NSField>
              </div>
              {errors.profile && <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--danger)', textAlign: 'center' }}>{errors.profile}</div>}
            </div>
          )}

          {step === 2 && (
            <div style={{ margin: 'auto 0', width: '100%' }}>
              <div className="eyebrow" style={{ textAlign: 'center' }}>Step 3 of 4</div>
              <h2 style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-0.02em', textAlign: 'center', marginTop: 6 }}>Add your brand</h2>
              <div style={{ marginTop: 22 }}>
                <NSDrop
                  icon={logoUploaded ? 'check' : 'image'}
                  title={uploadLogoMutation.isLoading ? 'Uploading…' : logoUploaded ? (formData.logo?.name || 'Logo uploaded') : 'Upload your logo'}
                  hint={logoUploaded ? "We'll use it across your site" : "SVG or PNG · we'll use it across your site"}
                  height={120}
                  accent={logoUploaded}
                  onFiles={handleLogoFiles}
                />
                {errors.logo && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)' }}>{errors.logo}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }}>
                  <NSField label="Primary colour">
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <label style={{ width: 40, height: 36, borderRadius: 9, background: formData.brand_colors[0], border: '1px solid var(--border-strong)', cursor: 'pointer', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                        <input type="color" value={formData.brand_colors[0]} onChange={e => setColor(0, e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
                      </label>
                      <input className="input mono" value={formData.brand_colors[0]} onChange={e => setColor(0, e.target.value)} />
                    </div>
                  </NSField>
                  <NSField label="Secondary colour">
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <label style={{ width: 40, height: 36, borderRadius: 9, background: formData.brand_colors[1], border: '1px solid var(--border-strong)', cursor: 'pointer', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                        <input type="color" value={formData.brand_colors[1]} onChange={e => setColor(1, e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
                      </label>
                      <input className="input mono" value={formData.brand_colors[1]} onChange={e => setColor(1, e.target.value)} />
                    </div>
                  </NSField>
                </div>
                {errors.brand_color && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)' }}>{errors.brand_color}</div>}
              </div>
            </div>
          )}

          {step === 3 && (
            <div style={{ textAlign: 'center', margin: 'auto 0' }}>
              <div style={{ width: 76, height: 76, borderRadius: '50%', margin: '0 auto 22px', background: 'var(--success-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--success)', fontSize: 36, animation: 'slideDown 0.35s cubic-bezier(0.22,1,0.36,1)' }}>
                <I.check />
              </div>
              <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' }}>Your workspace is ready</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, marginTop: 12, maxWidth: 380, marginInline: 'auto' }}>
                You're all set. Head to your dashboard to preview your site, request changes, and watch your traffic grow.
              </p>
              <button className="btn btn-primary" style={{ marginTop: 26, padding: '11px 22px', opacity: completeOnboardingMutation.isLoading ? 0.7 : 1 }} disabled={completeOnboardingMutation.isLoading} onClick={() => completeOnboardingMutation.mutate()}>
                {completeOnboardingMutation.isLoading ? 'Finishing…' : 'Go to dashboard'} <I.arrowRight />
              </button>
            </div>
          )}

          {/* footer nav */}
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 28 }}>
            <div style={{ width: 90 }}>
              {step > 0 && step < 3 && <button className="btn btn-sm btn-ghost" onClick={back}><I.arrowRight style={{ transform: 'rotate(180deg)' }} /> Back</button>}
              {step === 0 && <button className="btn btn-sm btn-ghost" onClick={onClose} style={{ color: 'var(--text-muted)' }}>Skip setup</button>}
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 7 }}>
              {Array.from({ length: STEPS }).map((_, i) => (
                <span key={i} style={{ width: i === step ? 22 : 7, height: 7, borderRadius: 999, background: i === step ? 'var(--accent)' : 'var(--surface-2)', transition: 'all 0.25s ease' }} />
              ))}
            </div>
            <div style={{ width: 90, display: 'flex', justifyContent: 'flex-end' }}>
              {step === 1 && (
                <button className="btn btn-sm btn-primary" onClick={handleCompanyNext} disabled={savingCompany} style={{ opacity: savingCompany ? 0.7 : 1 }}>
                  {savingCompany ? 'Saving…' : 'Next'} <I.arrowRight />
                </button>
              )}
              {step === 2 && (
                <button className="btn btn-sm btn-primary" onClick={handleBrandNext} disabled={savingBrand} style={{ opacity: savingBrand ? 0.7 : 1 }}>
                  {savingBrand ? 'Saving…' : 'Next'} <I.arrowRight />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingModal
